import { Layers } from 'lucide-react';
import { ErreurApi } from '../../lib/api';
import { useChoisirBloc, type SeanceAffichee } from '../../lib/useEmargement';
import { MessageErreur } from '../ui/Primitives';

/**
 * Bloc du programme que la feuille en salle dispense (TF-461) : il range ses
 * demi-journées dans le taux d'absentéisme par module. Facultatif, au choix de
 * qui tient la feuille jusqu'à sa clôture ; ensuite, le pôle pédagogique et la
 * direction le rangent encore, les autres le lisent (`refusBloc` de l'API).
 * Une classe à distance reçoit le sien de sa salle, une feuille sans formation
 * n'a pas de programme : rien ne s'affiche alors.
 */
export function ChoixBloc({
  seance,
  onMiseAJour,
  className = '',
}: {
  seance: SeanceAffichee;
  onMiseAJour?: (seance: SeanceAffichee) => void;
  className?: string;
}): JSX.Element | null {
  const choisir = useChoisirBloc();
  if (seance.type === 'ELEARNING' || (seance.blocs.length === 0 && seance.bloc === null)) return null;

  const libelle = seance.bloc?.libelle ?? 'Hors bloc';
  if (seance.refusBloc) {
    return (
      <p className={`flex items-start gap-1.5 text-xs text-codialis-gray ${className}`}>
        <Layers className="mt-px h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        <span className="min-w-0">Bloc dispensé : {libelle}</span>
      </p>
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      <label className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:gap-3">
        <span className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-codialis-navy">
          <Layers className="h-3.5 w-3.5" aria-hidden="true" />
          Bloc dispensé
        </span>
        <select
          value={seance.bloc?.id ?? ''}
          disabled={choisir.isPending}
          onChange={(evenement) =>
            choisir.mutate(
              { seanceId: seance.id, moduleFormationId: evenement.target.value || null },
              { onSuccess: (miseAJour) => onMiseAJour?.(miseAJour) },
            )
          }
          className="min-h-[44px] w-full min-w-0 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-codialis-navy sm:w-auto sm:max-w-md"
        >
          <option value="">Hors bloc</option>
          {seance.blocs.map((bloc) => (
            <option key={bloc.id} value={bloc.id}>
              {bloc.libelle}
            </option>
          ))}
        </select>
      </label>
      {seance.statut === 'CLOTUREE' && (
        <p className="text-[11px] text-codialis-gray">
          Feuille clôturée : le bloc est un classement, il reste modifiable et chaque changement est tracé.
        </p>
      )}
      {choisir.error && (
        <MessageErreur
          message={choisir.error instanceof ErreurApi ? choisir.error.message : 'Le bloc n’a pas pu être enregistré.'}
        />
      )}
    </div>
  );
}
